import { useEffect, useRef, useState } from "react";
import styles from "../../ConversionAndPdpViewer.module.css";
import { MAIN_TABS, RANKING_TABS } from "./constants";
import {
  buildComparisonSubtext,
  formatNumber,
  formatPct,
} from "./formatters";

let TONE_CLASS;
const getToneClass = () => {
  if (!TONE_CLASS) {
    TONE_CLASS = {
      positive: styles.positive,
      warning: styles.warning,
      negative: styles.negative,
      neutral: styles.neutral,
    };
  }
  return TONE_CLASS;
};

export function PeriodPicker({ windows, selectedIdx, onSelect, id }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const selected = windows[selectedIdx] || windows[0];

  return (
    <div className={styles.ddpWrap} ref={wrapRef}>
      <button
        className={styles.ddpTrigger}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Period picker ${id}`}
      >
        <span className={styles.ddpTriggerLabel}>{selected?.label}</span>
        <span
          className={`${styles.ddpTriggerChevron} ${open ? styles.ddpTriggerChevronOpen : ""}`}
        >
          ▾
        </span>
      </button>
      {open && (
        <div className={styles.ddpDropdown} role="listbox">
          {windows.length === 0 ? (
            <div className={styles.ddpEmpty}>No periods available</div>
          ) : (
            windows.map((w, idx) => (
              <button
                key={w.startDate}
                role="option"
                aria-selected={idx === selectedIdx}
                className={`${styles.ddpItem} ${idx === selectedIdx ? styles.ddpItemActive : ""}`}
                onClick={() => {
                  onSelect(idx);
                  setOpen(false);
                }}
              >
                {w.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export function MetricCard({ label, value, subtext, tone = "neutral" }) {
  return (
    <div className={styles.metricCard}>
      <div className={styles.metricLabel}>{label}</div>
      <div
        className={`${styles.metricValue} ${
          tone === "positive"
            ? styles.positive
            : tone === "negative"
              ? styles.negative
              : tone === "warning"
                ? styles.warning
                : ""
        }`}
      >
        {value}
      </div>
      {subtext ? <div className={styles.metricSubtext}>{subtext}</div> : null}
    </div>
  );
}

export function SummaryBanner({ title, text, tone = "warning" }) {
  return (
    <div
      className={`${styles.summaryBanner} ${
        tone === "positive"
          ? styles.summaryBannerPositive
          : tone === "negative"
            ? styles.summaryBannerNegative
            : styles.summaryBannerWarning
      }`}
    >
      <div className={styles.summaryBannerLabel}>{title}</div>
      <p className={styles.summaryBannerText}>{text}</p>
    </div>
  );
}

export function MainTabs({ activeTab, onChange }) {
  return (
    <div className={styles.tabBar}>
      {MAIN_TABS.map((tab) => (
        <button
          key={tab.key}
          type="button"
          className={`${styles.tabBtn} ${activeTab === tab.key ? styles.tabBtnActive : ""}`}
          onClick={() => onChange(tab.key)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

export function RankingTabs({ activeTab, onChange }) {
  return (
    <div className={styles.rankingTabBar}>
      {RANKING_TABS.map((tab) => (
        <button
          key={tab.key}
          type="button"
          className={`${styles.rankingTabBtn} ${activeTab === tab.key ? styles.rankingTabBtnActive : ""}`}
          onClick={() => onChange(tab.key)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

export function Waterfall({ stages = [] }) {
  const maxValue = Math.max(...stages.map((s) => Number(s.value || 0)), 1);
  const transitions = stages.map((stage, i) => {
    if (i === stages.length - 1) return null;
    const current = Number(stage.value || 0);
    const next = Number(stages[i + 1]?.value || 0);
    const rate = current > 0 ? (next / current) * 100 : 0;
    const lost = current - next;
    return { rate, lost };
  });

  return (
    <div className={styles.waterfallWrap}>
      <div className={styles.waterfall}>
        {stages.map((stage, index) => {
          const height = Math.max(
            24,
            (Number(stage.value || 0) / maxValue) * 220,
          );
          const transition = transitions[index];

          return (
            <div key={stage.key || stage.label || index} className={styles.waterfallChunk}>
              <div className={styles.waterfallStage}>
                <div className={styles.waterfallValue}>{formatNumber(stage.value)}</div>
                <div
                  className={`${styles.waterfallBar} ${styles[`waterfallBar${index + 1}`] || ""}`}
                  style={{ height }}
                />
                <div className={styles.waterfallLabel}>{stage.label}</div>
              </div>

              {transition ? (
                <div className={styles.waterfallTransition}>
                  <div className={styles.waterfallArrow}>→</div>
                  <div
                    className={`${styles.waterfallRate} ${
                      transition.rate < 20
                        ? styles.negative
                        : transition.rate < 60
                          ? styles.warning
                          : styles.positive
                    }`}
                  >
                    {formatPct(transition.rate)}
                  </div>
                  <div className={styles.waterfallLost}>
                    -{formatNumber(transition.lost)}
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export const comparison = {
  buildComparisonSubtext,
  getToneClass,
};
